import { Injectable, inject } from '@angular/core';
import { Observable, map, switchMap, throwError, from, take, firstValueFrom, tap } from 'rxjs';
import { Cashier, CashierTransaction } from '../models/cashier.model';
import { NewDoc, QuerySpec, TenantDb } from '../data/tenant-collection';
import { BusinessContextService } from './multi-tenant/business-context.service';
import { EmployeeService } from './employee.service';

@Injectable({
  providedIn: 'root',
})
export class CashRegisterService {
  /**
   * All Firestore access goes through the TenantCollection seam — tenant scoping,
   * injection context, audit fields, and the Timestamp<->Date codec live there, not here.
   * `cash_registers` is a mutable collection (open/close/balance updates), so it uses the
   * full-CRUD `collection()` surface. Public methods keep their Observable surface by
   * wrapping the seam's Promises at the edge. ALL balance/money logic is preserved exactly.
   */
  private registers = inject(TenantDb).collection<Cashier>('cash_registers');
  private businessContext = inject(BusinessContextService);
  private employeeService = inject(EmployeeService);

  /**
   * Get all open cash registers for current business and current location/store
   * One-time fetch - does not listen for real-time updates.
   * When a location is selected it filters by locationId; otherwise lists all open
   * registers (preserving the original behavior).
   */
  getOpenCashRegisters(): Observable<Cashier[]> {
    return this.businessContext.currentLocationId$.pipe(
      take(1),
      switchMap(locationId => {
        const where: QuerySpec['where'] = locationId
          ? [
              ['locationId', '==', locationId],
              ['status', '==', 'open'],
            ]
          : [['status', '==', 'open']];
        return from(
          this.registers.list({
            where,
            orderBy: { field: 'openedAt', direction: 'desc' },
          })
        );
      })
    );
  }

  /**
   * Get cash registers by location ID
   * One-time fetch - does not listen for real-time updates
   */
  getCashRegistersByLocation(locationId: string, status?: 'open' | 'closed'): Observable<Cashier[]> {
    const where: QuerySpec['where'] = status
      ? [
          ['locationId', '==', locationId],
          ['status', '==', status],
        ]
      : [['locationId', '==', locationId]];
    return from(
      this.registers.list({
        where,
        orderBy: { field: 'openedAt', direction: 'desc' },
        limit: 50,
      })
    );
  }

  /**
   * Get all cash registers (open and closed) for current business
   * One-time fetch - does not listen for real-time updates
   */
  getAllCashRegisters(limitCount: number = 50): Observable<Cashier[]> {
    return from(
      this.registers.list({
        orderBy: { field: 'openedAt', direction: 'desc' },
        limit: limitCount,
      })
    );
  }

  /**
   * Get cash registers by status
   * One-time fetch - does not listen for real-time updates
   */
  getCashRegistersByStatus(status: 'open' | 'closed'): Observable<Cashier[]> {
    return from(
      this.registers.list({
        where: [['status', '==', status]],
        orderBy: { field: 'openedAt', direction: 'desc' },
        limit: 50,
      })
    );
  }

  /**
   * Get a specific cash register by ID
   * One-time fetch - does not listen for real-time updates
   */
  getCashRegisterById(registerId: string): Observable<Cashier | undefined> {
    return from(this.registers.get(registerId));
  }

  /**
   * Get cash registers opened by specific user
   * One-time fetch - does not listen for real-time updates
   */
  getCashRegistersByUser(userId: string): Observable<Cashier[]> {
    return from(
      this.registers.list({
        where: [['openedBy', '==', userId]],
        orderBy: { field: 'openedAt', direction: 'desc' },
        limit: 20,
      })
    );
  }

  /**
   * Open a new cash register.
   * Uses a deterministic ID based on location, user, and timestamp to prevent
   * duplicate cash registers when offline writes sync on reconnect. Uses set()
   * so a re-synced offline write overwrites with identical data instead of dup-ing.
   */
  openCashRegister(register: Omit<Cashier, 'id'>): Observable<string> {
    // Generate a deterministic ID based on location, user, and minute-level timestamp.
    // This ensures the same cash register creation attempt produces the same ID.
    const timestamp = register.openedAt;
    const dateStr = `${timestamp.getFullYear()}${String(timestamp.getMonth() + 1).padStart(2, '0')}${String(timestamp.getDate()).padStart(2, '0')}`;
    const timeStr = `${String(timestamp.getHours()).padStart(2, '0')}${String(timestamp.getMinutes()).padStart(2, '0')}`;
    const deterministicId = `${register.locationId}-${register.openedBy}-${dateStr}-${timeStr}`;

    // openedAt stays a Date (codec converts to Timestamp); audit fields are seam-owned.
    const registerData = {
      ...register,
      status: 'open' as const,
      type: 'main',
    } as NewDoc<Cashier>;

    return from(this.registers.set(deterministicId, registerData)).pipe(map(() => deterministicId));
  }

  /**
   * Close a cash register
   */
  closeCashRegister(
    registerId: string,
    closingData: {
      closedBy: string;
      closedByName: string;
      closingAmount: number;
      closedAt: Date;
      expectedAmount?: number;
      difference?: number;
      closingNotes?: string;
    }
  ): Observable<void> {
    // closedAt stays a Date (codec -> Timestamp); updatedAt is seam-owned.
    const patch = {
      ...closingData,
      status: 'closed' as const,
    } as Partial<NewDoc<Cashier>>;

    return from(this.registers.update(registerId, patch)).pipe(
      // After closing the register, validate commission thresholds
      tap(() => {
        // Run threshold validation asynchronously (don't block the close operation)
        this.validateCommissionThresholds(registerId);
      })
    );
  }

  /**
   * Validate commission thresholds for a closed session/register
   * Marks commissions as 'invalid' if threshold not met
   */
  private async validateCommissionThresholds(sessionId: string): Promise<void> {
    try {
      await firstValueFrom(this.employeeService.validateSessionCommissionThresholds(sessionId));
    } catch (error) {
      console.error('[CashRegisterService] Error validating commission thresholds:', error);
    }
  }

  /**
   * Add transaction to cash register.
   * Read-modify-write of the transactions array and running balance. NOT a Firestore
   * transaction — preserves the original behavior exactly (last-write-wins on the array).
   */
  addTransaction(registerId: string, transaction: CashierTransaction): Observable<void> {
    return this.getCashRegisterById(registerId).pipe(
      take(1), // Take only the first emission, don't listen for real-time updates
      switchMap(register => {
        if (!register) {
          return throwError(() => new Error('Cash register not found'));
        }

        const transactions = register.transactions || [];
        const transactionData = {
          ...transaction,
          id: `tx_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
          // createdAt stays a Date — the codec converts it to a Timestamp on write.
          createdAt: transaction.createdAt,
        };

        transactions.push(transactionData);

        // Update current amount based on transaction type
        let currentAmount = register.currentAmount || 0;

        switch (transaction.type) {
          case 'sale':
          case 'deposit':
          case 'opening':
            currentAmount += transaction.amount;
            break;
          case 'withdrawal':
          case 'pickup':
            currentAmount -= transaction.amount;
            break;
        }

        return from(
          this.registers.update(registerId, {
            transactions,
            currentAmount,
          } as Partial<NewDoc<Cashier>>)
        );
      })
    );
  }

  /**
   * Update cash register current amount
   */
  updateCurrentAmount(registerId: string, amount: number): Observable<void> {
    return from(
      this.registers.update(registerId, { currentAmount: amount } as Partial<NewDoc<Cashier>>)
    );
  }

  /**
   * Update cash register expected amount (calculated from sales)
   */
  updateExpectedAmount(registerId: string, expectedAmount: number): Observable<void> {
    return from(
      this.registers.update(registerId, { expectedAmount } as Partial<NewDoc<Cashier>>)
    );
  }

  /**
   * Get cash register statistics
   */
  getCashRegisterStats(registerId: string): Observable<{
    totalSales: number;
    totalTransactions: number;
    salesCount: number;
    depositsCount: number;
    withdrawalsCount: number;
  }> {
    return this.getCashRegisterById(registerId).pipe(
      map(register => {
        if (!register) {
          return {
            totalSales: 0,
            totalTransactions: 0,
            salesCount: 0,
            depositsCount: 0,
            withdrawalsCount: 0,
          };
        }

        const transactions = register.transactions || [];

        return {
          totalSales: transactions
            .filter(t => t.type === 'sale')
            .reduce((sum, t) => sum + t.amount, 0),
          totalTransactions: transactions.length,
          salesCount: transactions.filter(t => t.type === 'sale').length,
          depositsCount: transactions.filter(t => t.type === 'deposit').length,
          withdrawalsCount: transactions.filter(t => t.type === 'withdrawal').length,
        };
      })
    );
  }
}
